<template>
  <!--
    ReferralField.vue
    ─────────────────────────────────────────────────────────────────────────────
    Ultra-responsive referral code field component for all PettyCash forms
    (Basic Account, Business Suite).

    Props from parent (via referralMixin):
      v-model          → referralCode
      :state           → referralState  ('idle'|'loading'|'valid'|'invalid'|'manual')
      :ownerName       → referralOwnerName
      :locked          → referralLocked
      @input           → onReferralInput

    Usage:
      <ReferralField
        v-model="referralCode"
        :state="referralState"
        :ownerName="referralOwnerName"
        :locked="referralLocked"
        @input="onReferralInput"
      />
    ─────────────────────────────────────────────────────────────────────────────
  -->
  <div class="pc-referral-wrapper" :class="`state--${state}`">

    <!-- ── Label ── -->
    <label class="pc-referral-label">
      <span class="pc-label-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
        </svg>
      </span>
      Referral Code
      <span class="pc-label-optional">(optional)</span>
    </label>

    <!-- ── Champ principal ── -->
    <div class="pc-referral-field-wrap">

      <!-- Icône gauche : état dynamique -->
      <div class="pc-field-icon-left" :class="`icon--${state}`">
        <!-- idle / manual -->
        <svg v-if="state === 'idle' || state === 'manual'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>

        <!-- loading : spinner SVG animé -->
        <svg v-else-if="state === 'loading'" class="pc-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
            stroke-dasharray="44" stroke-dashoffset="30"/>
        </svg>

        <!-- valid : cadenas ouvert → fermé animé -->
        <div v-else-if="state === 'valid'" class="pc-icon-valid">
          <svg class="pc-checkmark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>

        <!-- invalid : croix -->
        <svg v-else-if="state === 'invalid'" class="pc-shake-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </div>

      <!-- ── INPUT ou BADGE VERROUILLÉ ── -->
      <transition name="pc-field-swap" mode="out-in">

        <!-- Mode VERROUILLÉ + VALIDE : on masque le champ, on affiche le badge -->
        <div v-if="locked && state === 'valid'" key="locked-badge" class="pc-locked-badge">
          <div class="pc-badge-inner">
            <div class="pc-badge-shimmer"></div>
            <span class="pc-badge-code notranslate" translate="no">{{ value }}</span>
            <div class="pc-badge-verified">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Verified
            </div>
          </div>
          <div v-if="ownerName" class="pc-owner-name">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Sponsor: <strong class="notranslate" translate="no">{{ ownerName }}</strong>
          </div>
        </div>

        <!-- Mode INPUT (idle, manual, loading, invalid, ou locked+invalid) -->
        <input
          v-else
          key="input-field"
          class="pc-referral-input"
          type="text"
          :value="value"
          :readonly="locked && state === 'loading'"
          :placeholder="locked ? 'Verifying...' : 'e.g. 020PC1'"
          autocomplete="off"
          spellcheck="false"
          @input="$emit('input', $event.target.value)"
        />

      </transition>

      <!-- Indicateur droit : état -->
      <div class="pc-field-icon-right">
        <transition name="pc-fade-scale" mode="out-in">
          <span v-if="state === 'loading'" key="r-loading" class="pc-right-tag pc-right-tag--loading">
            Checking…
          </span>
          <span v-else-if="state === 'valid'" key="r-valid" class="pc-right-tag pc-right-tag--valid">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 6.709l-11.285 11.29-5.285-5.291 1.414-1.414 3.871 3.877 9.871-9.877z"/></svg>
            Valid
          </span>
          <span v-else-if="state === 'invalid'" key="r-invalid" class="pc-right-tag pc-right-tag--invalid">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            Not Found
          </span>
        </transition>
      </div>
    </div>

    <!-- ── Barre de progression sous le champ ── -->
    <div class="pc-progress-bar">
      <div class="pc-progress-fill" :class="`fill--${state}`"></div>
    </div>

    <!-- ── Message contextuel ── -->
    <transition name="pc-msg-slide" mode="out-in">
      <p v-if="state === 'valid' && ownerName" key="msg-valid" class="pc-hint pc-hint--valid">
        🎉 Great! You'll be sponsored by <strong class="notranslate" translate="no">{{ ownerName }}</strong>.
      </p>
      <p v-else-if="state === 'valid' && !ownerName" key="msg-valid-anon" class="pc-hint pc-hint--valid">
        ✅ Referral code is valid and verified.
      </p>
      <p v-else-if="state === 'invalid'" key="msg-invalid" class="pc-hint pc-hint--invalid">
        ❌ This code doesn't exist. Please check and try again.
      </p>
      <p v-else-if="state === 'loading'" key="msg-loading" class="pc-hint pc-hint--loading">
        🔍 Verifying referral code…
      </p>
      <p v-else key="msg-idle" class="pc-hint pc-hint--idle">
        Enter the code shared by your sponsor, or leave empty.
      </p>
    </transition>

    <!-- ── Confetti (state valid) ── -->
    <div v-if="state === 'valid'" class="pc-confetti-container" aria-hidden="true">
      <span v-for="n in 12" :key="n" class="pc-confetti-dot" :style="confettiStyle(n)"></span>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ReferralField',
  props: {
    value: { type: String, default: '' },
    state: {
      type: String,
      default: 'idle',
      validator: v => ['idle', 'loading', 'valid', 'invalid', 'manual'].includes(v)
    },
    ownerName: { type: String, default: '' },
    locked: { type: Boolean, default: false },
  },

  methods: {
    confettiStyle (n) {
      const colors = ['#00C896', '#FFD700', '#00479B', '#FF6B6B', '#A855F7', '#F97316']
      const angle = (n / 12) * 360
      const radius = 28 + (n % 3) * 12
      const x = Math.cos((angle * Math.PI) / 180) * radius
      const y = Math.sin((angle * Math.PI) / 180) * radius
      return {
        '--x': `${x}px`,
        '--y': `${y}px`,
        '--color': colors[n % colors.length],
        '--delay': `${(n * 0.06).toFixed(2)}s`,
      }
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════════════════ */
.pc-referral-wrapper {
  --clr-idle:    #9CA3AF;
  --clr-loading: #3B82F6;
  --clr-valid:   #10B981;
  --clr-invalid: #EF4444;
  --clr-manual:  #6366F1;
  --radius: 12px;
  --transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  position: relative;
  margin-bottom: 1.25rem;
  font-family: 'DM Sans', -apple-system, sans-serif;
}

/* Mobile: Extra small screens */
@media (max-width: 479px) {
  .pc-referral-wrapper {
    margin-bottom: 0.875rem;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   LABEL
═══════════════════════════════════════════════════════════════════════════ */
.pc-referral-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  letter-spacing: 0.2px;
  user-select: none;
}

@media (max-width: 479px) {
  .pc-referral-label {
    font-size: 0.75rem;
    gap: 4px;
    margin-bottom: 6px;
  }
}

@media (max-width: 359px) {
  .pc-referral-label {
    font-size: 0.7rem;
    gap: 3px;
  }
}

.pc-label-icon {
  display: flex;
  color: #6B7280;
  flex-shrink: 0;
}

.pc-label-icon svg {
  width: 14px;
  height: 14px;
}

@media (max-width: 479px) {
  .pc-label-icon svg {
    width: 12px;
    height: 12px;
  }
}

.pc-label-optional {
  font-weight: 400;
  color: #9CA3AF;
  font-size: 0.75rem;
}

@media (max-width: 479px) {
  .pc-label-optional {
    font-size: 0.65rem;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   FIELD WRAPPER
═══════════════════════════════════════════════════════════════════════════ */
.pc-referral-field-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #F9FAFB;
  border: 2px solid #E5E7EB;
  border-radius: var(--radius);
  overflow: hidden;
  transition: border-color var(--transition), box-shadow var(--transition), background var(--transition);
  min-height: 44px;
}

@media (max-width: 479px) {
  .pc-referral-field-wrap {
    min-height: 40px;
    border-radius: 10px;
  }
}

@media (max-width: 359px) {
  .pc-referral-field-wrap {
    min-height: 36px;
    border-radius: 8px;
  }
}

/* État dynamique : border-color */
.state--loading .pc-referral-field-wrap { border-color: var(--clr-loading); background: #EFF6FF; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
.state--valid   .pc-referral-field-wrap { border-color: var(--clr-valid);   background: #F0FDF4; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); }
.state--invalid .pc-referral-field-wrap { border-color: var(--clr-invalid); background: #FEF2F2; box-shadow: 0 0 0 3px rgba(239,68,68,0.1); animation: pc-shake 0.45s ease; }
.state--manual  .pc-referral-field-wrap { border-color: var(--clr-manual);  box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.state--idle    .pc-referral-field-wrap:focus-within { border-color: #6366F1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }

@media (max-width: 479px) {
  .state--loading .pc-referral-field-wrap,
  .state--valid .pc-referral-field-wrap,
  .state--invalid .pc-referral-field-wrap,
  .state--manual .pc-referral-field-wrap,
  .state--idle .pc-referral-field-wrap:focus-within { 
    box-shadow: 0 0 0 2px rgba(59,130,246,0.1);
  }
}

/* ── Icône gauche ── */
.pc-field-icon-left {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  transition: color var(--transition);
}

@media (max-width: 479px) {
  .pc-field-icon-left {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 359px) {
  .pc-field-icon-left {
    width: 36px;
    height: 36px;
  }
}

.icon--idle,
.icon--manual  { color: #9CA3AF; }
.icon--loading { color: var(--clr-loading); }
.icon--valid   { color: var(--clr-valid); }
.icon--invalid { color: var(--clr-invalid); }

.pc-field-icon-left svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 479px) {
  .pc-field-icon-left svg {
    width: 16px;
    height: 16px;
  }
}

@media (max-width: 359px) {
  .pc-field-icon-left svg {
    width: 14px;
    height: 14px;
  }
}

/* ── Input ── */
.pc-referral-input {
  flex: 1;
  height: 44px;
  padding: 0 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 1.2px;
  color: #1F2937;
  background: transparent;
  border: none;
  outline: none;
  text-transform: uppercase;
  min-width: 0;
}

@media (max-width: 479px) {
  .pc-referral-input {
    height: 40px;
    font-size: 0.875rem;
    letter-spacing: 1px;
    padding: 0 6px;
  }
}

@media (max-width: 359px) {
  .pc-referral-input {
    height: 36px;
    font-size: 0.8125rem;
    letter-spacing: 0.8px;
    padding: 0 5px;
  }
}

.pc-referral-input::placeholder {
  color: #C4CAD4;
  font-weight: 400;
  letter-spacing: 0.3px;
  text-transform: none;
  font-family: 'DM Sans', sans-serif;
}

/* ── Badge verrouillé (état valid + locked) ── */
.pc-locked-badge {
  flex: 1;
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  min-width: 0;
}

@media (max-width: 479px) {
  .pc-locked-badge {
    padding: 0 6px;
    gap: 2px;
  }
}

@media (max-width: 359px) {
  .pc-locked-badge {
    padding: 0 5px;
  }
}

.pc-badge-inner {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #D1FAE5, #A7F3D0);
  border-radius: 50px;
  padding: 5px 14px 5px 10px;
  overflow: hidden;
  width: fit-content;
  min-width: 0;
}

@media (max-width: 479px) {
  .pc-badge-inner {
    gap: 6px;
    padding: 4px 10px 4px 8px;
  }
}

@media (max-width: 359px) {
  .pc-badge-inner {
    gap: 4px;
    padding: 3px 8px 3px 6px;
  }
}

.pc-badge-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
  animation: pc-shimmer 2.2s infinite;
}

.pc-badge-code {
  font-size: 0.875rem;
  font-weight: 700;
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 1.5px;
  color: #065F46;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

@media (max-width: 479px) {
  .pc-badge-code {
    font-size: 0.8rem;
    letter-spacing: 1.2px;
  }
}

@media (max-width: 359px) {
  .pc-badge-code {
    font-size: 0.75rem;
    letter-spacing: 1px;
  }
}

.pc-badge-verified {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

@media (max-width: 479px) {
  .pc-badge-verified {
    font-size: 0.6rem;
    gap: 2px;
    letter-spacing: 0.3px;
  }
}

@media (max-width: 359px) {
  .pc-badge-verified {
    display: none;
  }
}

.pc-badge-verified svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

@media (max-width: 479px) {
  .pc-badge-verified svg {
    width: 10px;
    height: 10px;
  }
}

.pc-owner-name {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.74rem;
  color: #6B7280;
  padding-left: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 479px) {
  .pc-owner-name {
    font-size: 0.65rem;
    gap: 3px;
    padding-left: 2px;
  }
}

@media (max-width: 359px) {
  .pc-owner-name {
    display: none;
  }
}

.pc-owner-name svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

@media (max-width: 479px) {
  .pc-owner-name svg {
    width: 10px;
    height: 10px;
  }
}

.pc-owner-name strong {
  color: #065F46;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Icône droite ── */
.pc-field-icon-right {
  display: flex;
  align-items: center;
  padding-right: 10px;
  flex-shrink: 0;
}

@media (max-width: 479px) {
  .pc-field-icon-right {
    padding-right: 8px;
  }
}

@media (max-width: 359px) {
  .pc-field-icon-right {
    padding-right: 6px;
  }
}

.pc-right-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 50px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
}

@media (max-width: 479px) {
  .pc-right-tag {
    padding: 2px 7px;
    font-size: 0.6rem;
    gap: 3px;
  }
}

@media (max-width: 359px) {
  .pc-right-tag {
    padding: 2px 5px;
    font-size: 0.55rem;
    gap: 2px;
  }
}

.pc-right-tag svg {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}

@media (max-width: 479px) {
  .pc-right-tag svg {
    width: 8px;
    height: 8px;
  }
}

.pc-right-tag--loading { background: #DBEAFE; color: #1D4ED8; }
.pc-right-tag--valid   { background: #D1FAE5; color: #065F46; }
.pc-right-tag--invalid { background: #FEE2E2; color: #991B1B; }

/* ═══════════════════════════════════════════════════════════════════════════
   SPINNER
═══════════════════════════════════════════════════════════════════════════ */
.pc-spinner {
  animation: pc-spin 0.9s linear infinite;
  transform-origin: center;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CHECKMARK (valid)
═══════════════════════════════════════════════════════════════════════════ */
.pc-icon-valid { position: relative; display: flex; }

.pc-checkmark {
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation: pc-draw-check 0.4s 0.1s ease forwards;
  color: var(--clr-valid);
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHAKE (invalid icon)
═══════════════════════════════════════════════════════════════════════════ */
.pc-shake-icon { animation: pc-shake-icon 0.35s ease; }

/* ═══════════════════════════════════════════════════════════════════════════
   BARRE DE PROGRESSION
═══════════════════════════════════════════════════════════════════════════ */
.pc-progress-bar {
  height: 2px;
  background: #E5E7EB;
  border-radius: 0 0 var(--radius) var(--radius);
  overflow: hidden;
  margin-top: -2px;
}

@media (max-width: 479px) {
  .pc-progress-bar {
    height: 1.5px;
  }
}

.pc-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.5s var(--transition), background 0.35s;
}

.fill--idle    { width: 0%;    background: #9CA3AF; }
.fill--manual  { width: 20%;   background: var(--clr-manual); }
.fill--loading { width: 60%;   background: var(--clr-loading); animation: pc-progress-pulse 1.1s ease-in-out infinite; }
.fill--valid   { width: 100%;  background: var(--clr-valid); }
.fill--invalid { width: 100%;  background: var(--clr-invalid); }

/* ═══════════════════════════════════════════════════════════════════════════
   MESSAGES CONTEXTUELS
═══════════════════════════════════════════════════════════════════════════ */
.pc-hint {
  margin: 6px 0 0 2px;
  font-size: 0.775rem;
  line-height: 1.4;
  transition: color var(--transition);
  word-break: break-word;
}

@media (max-width: 479px) {
  .pc-hint {
    margin: 4px 0 0 2px;
    font-size: 0.7rem;
    line-height: 1.3;
  }
}

@media (max-width: 359px) {
  .pc-hint {
    margin: 3px 0 0 1px;
    font-size: 0.65rem;
  }
}

.pc-hint--idle    { color: #9CA3AF; }
.pc-hint--loading { color: var(--clr-loading); }
.pc-hint--valid   { color: #059669; font-weight: 500; }
.pc-hint--invalid { color: var(--clr-invalid); font-weight: 500; }

/* ═══════════════════════════════════════════════════════════════════════════
   CONFETTI
═══════════════════════════════════════════════════════════════════════════ */
.pc-confetti-container {
  position: absolute;
  top: 50%;
  left: 20px;
  pointer-events: none;
  z-index: 10;
}

@media (max-width: 479px) {
  .pc-confetti-container {
    left: 18px;
  }
}

@media (max-width: 359px) {
  .pc-confetti-container {
    left: 16px;
  }
}

.pc-confetti-dot {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color);
  opacity: 0;
  animation: pc-confetti-burst 0.65s var(--delay) ease-out forwards;
}

@media (max-width: 479px) {
  .pc-confetti-dot {
    width: 6px;
    height: 6px;
  }
}

@media (max-width: 359px) {
  .pc-confetti-dot {
    width: 5px;
    height: 5px;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   VUE TRANSITIONS
═══════════════════════════════════════════════════════════════════════════ */
/* Swap champ ↔ badge */
.pc-field-swap-enter-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.pc-field-swap-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; position: absolute; width: 100%; }
.pc-field-swap-enter        { opacity: 0; transform: translateY(6px); }
.pc-field-swap-leave-to     { opacity: 0; transform: translateY(-6px); }

/* Fade + scale pour tags droits */
.pc-fade-scale-enter-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.pc-fade-scale-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; position: absolute; }
.pc-fade-scale-enter        { opacity: 0; transform: scale(0.7); }
.pc-fade-scale-leave-to     { opacity: 0; transform: scale(1.15); }

/* Slide message */
.pc-msg-slide-enter-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.pc-msg-slide-leave-active { transition: opacity 0.15s ease; position: absolute; }
.pc-msg-slide-enter        { opacity: 0; transform: translateY(-4px); }
.pc-msg-slide-leave-to     { opacity: 0; }

/* ═══════════════════════════════════════════════════════════════════════════
   KEYFRAMES
═══════════════════════════════════════════════════════════════════════════ */
@keyframes pc-spin {
  to { transform: rotate(360deg); }
}

@keyframes pc-draw-check {
  to { stroke-dashoffset: 0; }
}

@keyframes pc-shake {
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-5px); }
  40%       { transform: translateX(5px); }
  60%       { transform: translateX(-4px); }
  80%       { transform: translateX(3px); }
}

@keyframes pc-shake-icon {
  0%, 100% { transform: rotate(0deg); }
  25%       { transform: rotate(-15deg); }
  75%       { transform: rotate(15deg); }
}

@keyframes pc-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}

@keyframes pc-progress-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.55; }
}

@keyframes pc-confetti-burst {
  0%   { opacity: 1; transform: translate(0, 0) scale(1); }
  100% { opacity: 0; transform: translate(var(--x), var(--y)) scale(0.4); }
}

/* ═══════════════════════════════════════════════════════════════════════════
   TABLET BREAKPOINTS (768px+)
═══════════════════════════════════════════════════════════════════════════ */
@media (min-width: 768px) and (max-width: 1023px) {
  /* Tablet optimizations - mostly inherit from base desktop */
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP BREAKPOINTS (1024px+)
═══════════════════════════════════════════════════════════════════════════ */
@media (min-width: 1024px) {
  .pc-referral-wrapper {
    --radius: 12px;
  }
}
</style>
